import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, RadioGroup, FormControlLabel, Radio, Button } from '@mui/material';
import { styled } from '@mui/system';
import TaskIcon from '@mui/icons-material/Assignment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DoneIcon from '@mui/icons-material/Done';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { getFirestore, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import useWorkHoursSummary from './WorkHoursSummary';
import MonthlyHoursDialog from './MonthlyHoursDialog';
import AttendanceChart from './AttendanceChart';
import RemainingAnnualLeaves from './RemainingAnnualLeaves';
import WorkOffIcon from '@mui/icons-material/WorkOff';


export const Item = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'left',
  color: theme.palette.text.secondary,
  boxShadow: 'none',
  position: 'relative',
}));

const EDashboard = ({ user }) => {
  const { totalHours, avgHours } = useWorkHoursSummary(user);
  const [selectedOption, setSelectedOption] = useState('daily');
  const [totalTasks, setTotalTasks] = useState(0);
  const [completedTasks, setCompletedTasks] = useState(0);
  const [latestMonthlyHours, setLatestMonthlyHours] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const db = getFirestore();

  useEffect(() => {
    // Fetch tasks from Firestore
    const fetchTasks = async () => {
      if (!user || !user.id) return;

      const tasksRef = collection(db, 'assignedTasks');
      const q = query(tasksRef, where('employeeId', '==', user.id));
      const querySnapshot = await getDocs(q);

      const totalTasksCount = querySnapshot.size;
      const completedTasksCount = querySnapshot.docs.filter(doc => doc.data().status === 'completed').length;

      setTotalTasks(totalTasksCount);
      setCompletedTasks(completedTasksCount);
    };

    fetchTasks();
  }, [user, db]);

  useEffect(() => {
    const fetchLatestMonthlyHours = async () => {
      if (!user || !user.id) return;
    
      const monthlyHoursRef = collection(db, 'employeeMonthlyHours');
      const q = query(
        monthlyHoursRef,
        where('employeeId', '==', user.id),
        orderBy('timestamp', 'desc'), // Order by timestamp instead of month
        limit(1) // Get only the latest entry
      );
      const querySnapshot = await getDocs(q);
    
      if (!querySnapshot.empty) {
        const latestDoc = querySnapshot.docs[0].data();
        setLatestMonthlyHours({
          month: latestDoc.month, // Display the latest month
          monthlyHours: latestDoc.monthlyHours.split(" ")[0], // Extract numeric hours
        });
      }
    };
    

    fetchLatestMonthlyHours();
  }, [user, db]);

  const handleRadioChange = (event) => setSelectedOption(event.target.value);

  const handleOpenDialog = () => setDialogOpen(true);

  const handleCloseDialog = () => setDialogOpen(false);

  return (
    <Box sx={{ flexGrow: 1, padding: 3, paddingTop: 0, marginTop: 0 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#39B54A' }}>
          Dashboard
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {/* Monthly Hours section */}
        <Grid item xs={12} md={3}>
          <Item sx={{ height: 255, width: '100%', paddingTop:8 }}>
            <DateRangeIcon
              sx={{ color: '#39B54A', position: 'absolute', top: 8, right: 8, fontSize: 40 }}
             />
                <Typography variant="h4">
                  {latestMonthlyHours ? latestMonthlyHours.monthlyHours : ''} Monthly Hours
                </Typography>
                <Typography variant="h6">
                  {latestMonthlyHours ? latestMonthlyHours.month : 'Month'} 
                </Typography>
          </Item>
        </Grid>

        {/* Stats Section */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={2}>
            {/* First Row */}
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Item sx={{ height: 120, width: '100%' }}>
                  <TaskIcon
                    sx={{ color: '#39B54A', position: 'absolute', top: 8, right: 8, fontSize: 40 }}
                  />
                  <Typography variant="h4">{totalTasks}</Typography>
                  <Typography variant="h6">Total Tasks</Typography>
                </Item>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Item sx={{ height: 120, width: '100%' }}>
                  <AccessTimeIcon
                    sx={{ color: '#39B54A', position: 'absolute', top: 8, right: 8, fontSize: 40 }}
                  />
                  <Typography variant="h4">{totalHours.hours}</Typography>
                  <Typography variant="h6">Total Hours</Typography>
                </Item>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Item sx={{ height: 120, width: '100%' }}>
                  <WorkOffIcon
                    sx={{ color: '#39B54A', position: 'absolute', top: 8, right: 8, fontSize: 40 }}
                  />
                  <RemainingAnnualLeaves userId={user.id} />
                  <Typography variant="h6">
                    Remaining Annual Leaves 
                  </Typography>
                </Item>
              </Grid>
            </Grid>

            {/* Second Row */}
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Item sx={{ height: 120, width: '100%' }}>
                  <DoneIcon
                    sx={{ color: '#39B54A', position: 'absolute', top: 8, right: 8, fontSize: 40 }}
                  />
                  <Typography variant="h4">{completedTasks}</Typography>
                  <Typography variant="h6">Completed Tasks</Typography>
                </Item>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Item sx={{ height: 120, width: '100%' }}>
                  <WatchLaterIcon
                    sx={{ color: '#39B54A', position: 'absolute', top: 8, right: 8, fontSize: 40 }}
                  />
                  <Typography variant="h4">{avgHours.avgHours}</Typography>
                  <Typography variant="h6">Daily Average Hours</Typography>
                </Item>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Item sx={{ height: 120, width: '100%' }}>
                  <CalendarMonthIcon
                    sx={{ color: '#39B54A', position: 'absolute', top: 8, right: 8, fontSize: 40 }}
                  />
                  <Typography variant="h6">Monthly Hours</Typography>
                  <Typography>
                    <Button
                      variant="outlined"
                      sx={{
                        mt: 2,
                        color: '#39B54A',
                        borderColor: '#39B54A',
                        '&:hover': { borderColor: '#2c8f39', color: '#2c8f39' },
                      }}
                      onClick={handleOpenDialog}
                    >
                      Click to View
                    </Button>
                  </Typography>
                </Item>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Attendance Chart */}
        <Grid item xs={12}>
          <Item sx={{ height: 450, width: '100%', position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Attendance Chart</Typography>
              <RadioGroup
                row
                value={selectedOption}
                onChange={handleRadioChange}
                sx={{ marginRight: 2 }}
              >
                <FormControlLabel
                  value="daily"
                  control={
                    <Radio
                      sx={{
                        color: '#39B54A',
                        '&.Mui-checked': {
                          color: '#39B54A',
                        },
                      }}
                    />
                  }
                  label="Daily"
                />
                <FormControlLabel
                  value="weekly"
                  control={
                    <Radio
                      sx={{
                        color: '#39B54A',
                        '&.Mui-checked': {
                          color: '#39B54A',
                        },
                      }}
                    />
                  }
                  label="Weekly"
                />
                <FormControlLabel
                  value="monthly"
                  control={
                    <Radio
                      sx={{
                        color: '#39B54A',
                        '&.Mui-checked': {
                          color: '#39B54A',
                        },
                      }}
                    />
                  }
                  label="Monthly"
                />
              </RadioGroup>
            </Box>
            {/* Render the AttendanceChart component */}
            <AttendanceChart selectedOption={selectedOption} user={user} />
          </Item>
        </Grid>
      </Grid>

      {/* Monthly Hours Dialog */}
      {user && user.id && (
        <MonthlyHoursDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          user={user}
        />
      )}

    </Box>
  );
};

export default EDashboard;
